import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Send, Copy, XCircle } from "lucide-react";
import AuthFields from "./TokenInstanceFields";
import { uploadDocumentFile } from "../../../apis/documents";
import { toast } from "react-toastify";

const API_URL = `${process.env.REACT_APP_API_URL}/api/whatsapp/send-file`;

const LANGUAGE_SNIPPETS = ({
  number,
  imageUrl,
  message,
  token,
  instanceId,
}) => {
  const jsonBody = {
    phone: number || "<phoneNumber>",
    imageUrl: imageUrl || "<imageUrlId>",
    message: message || "<message>",
    instanceId: instanceId || "<instanceId>",
  };
  const jsonStr = JSON.stringify(jsonBody, null, 2);

  return {
    curl: `curl -X POST ${API_URL} \\
-H "Content-Type: application/json" \\
-H "Authorization: Bearer ${token || "<token>"}" \\
-d '${jsonStr}'`,

    node: `fetch("${API_URL}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${token || "<token>"}"
  },
  body: JSON.stringify(${JSON.stringify(jsonBody, null, 2)})
})`,

    python: `import requests

headers = {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${token || "<token>"}"
}

resp = requests.post("${API_URL}", json=${JSON.stringify(
      jsonBody,
      null,
      2
    )}, headers=headers)
print(resp.json())`,

    php: `<?php
$data = ${JSON.stringify(jsonBody, null, 2)};

$options = array(
  'http' => array(
    'header'  => "Content-type: application/json\\r\\n" .
                 "Authorization: Bearer ${token || "<token>"}\\r\\n",
    'method'  => 'POST',
    'content' => json_encode($data),
  ),
);
$context  = stream_context_create($options);
$result = file_get_contents('${API_URL}', false, $context);
?>`,

    javascript: `fetch("${API_URL}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${token || "<token>"}"
  },
  body: JSON.stringify(${JSON.stringify(jsonBody, null, 2)})
})`,
  };
};

const LANGUAGES = [
  { key: "curl", label: "cURL" },
  { key: "node", label: "Node.js" },
  { key: "python", label: "Python" },
  { key: "php", label: "PHP" },
  { key: "javascript", label: "JavaScript" },
];

const ImageDocumentContent = () => {
  // Auth fields
  const [inputToken, setInputToken] = useState("");
  const [inputInstanceId, setInputInstanceId] = useState("");
  const [token, setToken] = useState("");
  const [instanceId, setInstanceId] = useState("");
  const [editMode, setEditMode] = useState(true);

  // Input fields and UI states
  const [number, setNumber] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null); // null | 'success' | 'error'
  const [copied, setCopied] = useState(false);
  const snippetRef = useRef(null);
  const fileInputRef = useRef(null);

  // Save and Reset for Auth fields
  const handleSave = () => {
    setToken(inputToken);
    setInstanceId(inputInstanceId);
    setEditMode(false);
  };

  const handleReset = () => {
    setInputToken("");
    setInputInstanceId("");
    setToken("");
    setInstanceId("");
    setEditMode(true);
    setImageUrl("");
    setNumber("");
    setMessage("");
    setStatus(null);
    setCopied(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Upload file and get URL from backend
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }

    setUploading(true);
    setStatus(null);

    try {
      const response = await uploadDocumentFile(token, file);
      if (response.data?.success) {
        setImageUrl(response.data.data.doc_url);
        setStatus("success");
        toast.success("File uploaded successfully!");
      } else {
        setImageUrl("");
        setStatus("error");
        toast.error(response.data?.error || "File upload failed");
      }
    } catch (error) {
      setImageUrl("");
      setStatus("error");
      toast.error("Failed to upload file");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  // Copy API snippet to clipboard
  const handleCopy = () => {
    const snippet = LANGUAGE_SNIPPETS({
      number,
      imageUrl,
      message,
      token,
      instanceId,
    })[activeLang];

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(snippet).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
        toast.success("Snippet copied to clipboard!");
      });
    } else if (snippetRef.current) {
      const textArea = document.createElement("textarea");
      textArea.value = snippet;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
        toast.success("Snippet copied to clipboard!");
      } catch {
        toast.error("Failed to copy snippet");
      }
      document.body.removeChild(textArea);
    }
  };

  const [activeLang, setActiveLang] = useState("curl");

  const isUploadDisabled =
    uploading || editMode || !token.trim() || !instanceId.trim();

  return (
    <motion.div
      className="flex flex-col md:flex-row gap-6 p-6 bg-white rounded-xl shadow-lg"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, type: "spring" }}
    >
      <div className="w-full">
        {/* Auth fields */}
        <AuthFields
          inputInstanceId={inputInstanceId}
          setInputInstanceId={setInputInstanceId}
          inputToken={inputToken}
          setInputToken={setInputToken}
          instanceId={instanceId}
          token={token}
          editMode={editMode}
          setEditMode={setEditMode}
          onSave={handleSave}
          onReset={handleReset}
        />

        <div className="flex flex-col md:flex-row gap-6 mt-4">
          {/* Left pane: Upload form */}
          <div className="flex-1 max-w-md">
            <h3 className="mb-3 font-semibold text-lg text-gray-900">Upload File</h3>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              disabled={isUploadDisabled}
              className="block w-full border border-gray-300 rounded px-3 py-2 cursor-pointer"
            />

            {uploading && (
              <p className="mt-2 text-blue-600 flex items-center gap-1">
                <Send className="inline-block" /> Uploading...
              </p>
            )}

            {status === "success" && imageUrl && (
              <div className="mt-3 p-2 bg-green-50 border border-green-400 rounded flex justify-between items-center gap-3">
                <a
                  href={imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-700 underline break-all flex-1"
                  title="Open uploaded file"
                >
                  {imageUrl}
                </a>
                <button
                  onClick={handleCopy}
                  className="p-1 text-green-700 hover:text-green-900"
                  title="Copy API Snippet"
                  type="button"
                >
                  {copied ? (
                    <span className="text-green-900 font-semibold select-none">
                      Copied!
                    </span>
                  ) : (
                    <Copy size={20} />
                  )}
                </button>
                <button
                  onClick={handleReset}
                  className="ml-2 text-red-600 hover:text-red-800"
                  title="Clear"
                  type="button"
                >
                  <XCircle size={20} />
                </button>
              </div>
            )}

            {status === "error" && (
              <p className="mt-2 text-red-600 flex items-center gap-1">
                <XCircle className="inline-block" /> Error uploading file.
              </p>
            )}
          </div>

          {/* Right pane: API snippet and language selector */}
          <motion.div
            className="flex-2 md:w-3/5 bg-gray-50 p-4 rounded-lg shadow-inner flex flex-col"
            style={{ maxHeight: "520px" }}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <h4 className="mb-2 font-semibold flex-shrink-0 text-gray-900">
              API Documentation
            </h4>

            <motion.div
              className="mt-2 p-3 bg-white rounded-md border border-gray-300 shadow-sm text-gray-700 flex items-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              layout
            >
              <div className="flex items-center gap-2 leading-none">
                <strong className="text-black break-all font-mono">
                  POST:
                </strong>
                <code className="text-[#01CC54] break-all font-mono">
                  {API_URL}
                </code>
              </div>
            </motion.div>

            <div className="mt-3 flex gap-2 mb-2 flex-shrink-0">
              {LANGUAGES.map(({ key, label }) => (
                <button
                  key={key}
                  className={`px-2 py-1 rounded text-sm font-medium border ${
                    activeLang === key
                      ? "bg-[#048AAD] text-white"
                      : "bg-white text-gray-800 border-gray-200 hover:bg-gray-100"
                  }`}
                  onClick={() => setActiveLang(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div
              ref={snippetRef}
              className="mt-2 p-2 bg-gray-50 rounded mb-2 relative overflow-auto flex-grow border border-gray-200"
              style={{ minHeight: "120px" }}
            >
              <pre className="text-gray-900 text-sm p-3 m-0 whitespace-pre-wrap">
                {
                  LANGUAGE_SNIPPETS({
                    number,
                    imageUrl,
                    message,
                    token,
                    instanceId,
                  })[activeLang]
                }
              </pre>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default ImageDocumentContent;
